import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { jcqAPI, studentAPI, studentTutorialAPI } from '../services/api';
import { auth } from '../firebase';
import DemoHomeworkDetail, { TutorialReviewModal } from './demo/DemoHomeworkDetail';

const BRAND = {
  teal: '#75B9BE',
  cream: '#FDF6EC',
  charcoal: '#2D4042',
  coral: '#FF7F50',
  white: '#FFFFFF',
  muted: '#5D7375',
  grey: '#F0F2F5',
  success: '#2E7D32',
  danger: '#E07A5F',
  warning: '#F2CC8F',
  border: 'rgba(45,64,66,0.18)'
};

const JCQ_BUCKETS = [
  {
    id: 'BUCKET_A',
    title: 'Bucket A',
    subtitle: 'Extended Writing',
    subjects: ['English', 'History', 'Geography', 'Religious Studies'],
    aliases: ['Religious Education', 'Religious Studies (RS)']
  },
  {
    id: 'BUCKET_B',
    title: 'Bucket B',
    subtitle: 'STEM',
    subjects: ['Biology', 'Chemistry', 'Physics', 'Computer Science'],
    aliases: ['Computing']
  },
  {
    id: 'BUCKET_C',
    title: 'Bucket C',
    subtitle: 'Applied',
    subjects: ['Business Studies', 'Media Studies', 'Psychology', 'Sociology'],
    aliases: ['Business']
  }
];

const normalizeId = (item) => String(item?._id || item?.id || '');
const getBucketLetter = (bucketId = '') => String(bucketId).replace('BUCKET_', '');
const buildFallbackClassLink = (token = '') => {
  if (!token || typeof window === 'undefined') return '';
  return `${window.location.origin}/class-link/${token}`;
};

const hasAnyPublishedBucketLink = (linksByStudent = {}) => (
  Object.values(linksByStudent || {}).some((bucketLinks) => (
    Object.values(bucketLinks || {}).some((link) => Boolean(link?.url))
  ))
);
// Storage is scoped per teacher so one user's saved wizard state is never
// visible to another account on the same browser.
const getJcqFormStorageKey = () => `jcqFormState_${auth.currentUser?.uid || 'anon'}`;
const getSelectedLessonPlanIdByBucket = (selectedByBucket = {}) => Object.fromEntries(
  JCQ_BUCKETS.map((bucket) => [bucket.id, String(selectedByBucket?.[bucket.id]?.id || '')])
);

const loadSavedJcqFormState = () => {
  if (typeof window === 'undefined') return {};
  try {
    // Remove any legacy non-user-scoped state so it can never leak across accounts
    localStorage.removeItem('jcqFormState');
    sessionStorage.removeItem('jcqFormState');
    const saved = JSON.parse(localStorage.getItem(getJcqFormStorageKey()) || 'null');
    if (!saved || typeof saved !== 'object') return {};
    // Full state (including approvals and the published flag) persists across
    // reloads and logouts until the class is changed or selections are cleared.
    return saved;
  } catch {
    return {};
  }
};

const cardStyle = {
  background: BRAND.white,
  border: `2px solid ${BRAND.charcoal}`,
  borderRadius: 18,
  boxShadow: '6px 6px 0 rgba(45,64,66,0.12)',
  padding: 28
};

const stepBadgeStyle = {
  alignItems: 'center',
  background: BRAND.teal,
  border: `2px solid ${BRAND.charcoal}`,
  borderRadius: '999px',
  color: BRAND.charcoal,
  display: 'inline-flex',
  fontSize: '0.82rem',
  fontWeight: 900,
  gap: 8,
  letterSpacing: '0.04em',
  padding: '7px 12px',
  textTransform: 'uppercase'
};

const getMcqList = (mcqData) => {
  if (!mcqData) return [];
  if (Array.isArray(mcqData)) return mcqData;
  return [...(mcqData.easy || []), ...(mcqData.medium || []), ...(mcqData.hard || [])];
};

const renderText = (value, fallback = 'Not provided.') => (
  <span style={{ whiteSpace: 'pre-wrap' }}>{String(value || fallback)}</span>
);

const renderApprovalTutorialContent = (tutorial) => {
  const chunks = Array.isArray(tutorial?.generatedContent) ? tutorial.generatedContent : [];
  if (chunks.length === 0) {
    return (
      <div style={{ background: '#FFF8E1', border: `2px solid #F59E0B`, borderRadius: 12, color: '#1A1A2E', fontWeight: 800, padding: 18 }}>
        Preview content still loading…
      </div>
    );
  }
  return chunks.map((chunk, chunkIndex) => (
    <div key={chunkIndex} style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 12, marginBottom: 18, padding: 18 }}>
      <h4 style={{ color: '#0D9488', fontWeight: 900, marginBottom: 10, marginTop: 0 }}>Chunk {chunkIndex + 1}</h4>

      {chunk.narrativeExplainer && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ color: '#475569', fontWeight: 800, marginBottom: 4, textTransform: 'uppercase', fontSize: 11 }}>Narrative Explainer</div>
          <div style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 8, padding: 12 }}>
            {renderText(chunk.narrativeExplainer)}
          </div>
        </div>
      )}

      {chunk.keyTerminology && chunk.keyTerminology.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ color: '#475569', fontWeight: 800, marginBottom: 4, textTransform: 'uppercase', fontSize: 11 }}>Key Terminology</div>
          <div style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 8, padding: 12 }}>
            {chunk.keyTerminology.map((term, ti) => (
              <div key={ti} style={{ borderBottom: ti < chunk.keyTerminology.length - 1 ? '1px solid #F1F5F9' : 'none', paddingBottom: ti < chunk.keyTerminology.length - 1 ? 8 : 0, marginBottom: ti < chunk.keyTerminology.length - 1 ? 8 : 0 }}>
                <strong>{term.term}:</strong> {renderText(term.definition)}
              </div>
            ))}
          </div>
        </div>
      )}

      {chunk.multipleChoiceQuestions && getMcqList(chunk.multipleChoiceQuestions).length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ color: '#475569', fontWeight: 800, marginBottom: 4, textTransform: 'uppercase', fontSize: 11 }}>Multiple Choice Questions</div>
          {getMcqList(chunk.multipleChoiceQuestions).map((q, qi) => (
            <div key={qi} style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 8, marginBottom: 8, padding: 12 }}>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>{qi + 1}. {renderText(q.question)}</div>
              {(q.options || q.choices || []).map((opt, oi) => (
                <div key={oi} style={{ color: (opt === q.correctAnswer || oi === q.correctIndex) ? '#059669' : '#334155', fontWeight: (opt === q.correctAnswer || oi === q.correctIndex) ? 700 : 400, padding: '2px 0' }}>
                  {String.fromCharCode(65 + oi)}. {typeof opt === 'string' ? opt : opt.text || opt.label || JSON.stringify(opt)}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {chunk.sequencingActivity && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ color: '#475569', fontWeight: 800, marginBottom: 4, textTransform: 'uppercase', fontSize: 11 }}>Sequencing Activity</div>
          <div style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 8, padding: 12 }}>
            {chunk.sequencingActivity.question && <div style={{ marginBottom: 8 }}>{renderText(chunk.sequencingActivity.question)}</div>}
            {(chunk.sequencingActivity.items || chunk.sequencingActivity.steps || []).map((item, si) => (
              <div key={si} style={{ borderLeft: '3px solid #0D9488', marginBottom: 6, paddingLeft: 10 }}>
                {si + 1}. {renderText(typeof item === 'string' ? item : item.text || item.label)}
              </div>
            ))}
          </div>
        </div>
      )}

      {chunk.categorisationActivity && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ color: '#475569', fontWeight: 800, marginBottom: 4, textTransform: 'uppercase', fontSize: 11 }}>Categorisation Activity</div>
          <div style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 8, padding: 12 }}>
            {chunk.categorisationActivity.question && <div style={{ marginBottom: 8 }}>{renderText(chunk.categorisationActivity.question)}</div>}
            {Object.entries(chunk.categorisationActivity.categories || {}).map(([cat, items]) => (
              <div key={cat} style={{ marginBottom: 8 }}>
                <strong>{cat}:</strong> {Array.isArray(items) ? items.join(', ') : renderText(items)}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  ));
};

// ─── JCQ Evidence Dashboard (Report) ────────────────────────────────────────

const BUCKET_COLORS = {
  BUCKET_A: { bg: '#EEF6FF', border: '#93C5FD', text: '#1D4ED8' },
  BUCKET_B: { bg: '#F0FDF4', border: '#86EFAC', text: '#15803D' },
  BUCKET_C: { bg: '#FDF4FF', border: '#D8B4FE', text: '#7E22CE' }
};

export const BlueZoneBar = ({ stats }) => {
  if (!stats) {
    return <div style={{ color: BRAND.muted, fontSize: '0.8rem', fontWeight: 700 }}>Not attempted</div>;
  }
  const { standardTimeTotal, standardTimeCorrect, extraTimeTotal, extraTimeCorrect, totalQuestions } = stats;
  const stdWrong   = standardTimeTotal - standardTimeCorrect;
  const extraWrong = extraTimeTotal - extraTimeCorrect;
  const pct = (n) => `${((n / totalQuestions) * 100).toFixed(1)}%`;

  const segments = [
    { width: pct(standardTimeCorrect), bg: '#22C55E', title: `Standard time correct: ${standardTimeCorrect}` },
    { width: pct(stdWrong),            bg: '#E5E7EB', title: `Standard time wrong: ${stdWrong}` },
    { width: pct(extraTimeCorrect),    bg: '#3B82F6', title: `Extra time correct: ${extraTimeCorrect}` },
    { width: pct(extraWrong),          bg: '#BFDBFE', title: `Extra time wrong: ${extraWrong}` },
  ].filter(s => parseFloat(s.width) > 0);

  return (
    <div>
      <div style={{ display: 'flex', height: 20, borderRadius: 6, overflow: 'hidden', width: '100%', border: '1px solid #E5E7EB' }}>
        {segments.map((seg, i) => (
          <div key={i} title={seg.title} style={{ width: seg.width, background: seg.bg, transition: 'width 0.4s' }} />
        ))}
      </div>
      <div style={{ display: 'flex', gap: 12, marginTop: 5, flexWrap: 'wrap' }}>
        <span style={{ fontSize: '0.75rem', fontWeight: 800, color: '#15803D' }}>
          ✓ {standardTimeCorrect}/{standardTimeTotal} standard
        </span>
        {extraTimeTotal > 0 && (
          <span style={{ fontSize: '0.75rem', fontWeight: 800, color: '#1D4ED8' }}>
            🔵 +{extraTimeCorrect} in extra time
          </span>
        )}
        {stats.averageTimeSecs != null && (
          <span style={{ fontSize: '0.75rem', fontWeight: 700, color: BRAND.muted }}>
            {stats.averageTimeSecs}s avg/q
          </span>
        )}
      </div>
    </div>
  );
};

export const JCQPerformanceTrackerChart = ({ stats, isExtraTimeStudent, standardMaxSecs }) => {
  if (!stats || !stats.answers || stats.answers.length === 0) {
    return <div style={{ color: BRAND.muted, fontSize: '0.75rem', fontWeight: 700 }}>Not attempted</div>;
  }

  const { answers } = stats;

  // The configured standard test length (e.g. 15 min) defines the cutoff;
  // fall back to observed elapsed time for legacy attempts with no duration.
  const observedTotalSecs = answers.reduce((sum, a) => sum + (Number(a.timeTaken) || 0), 0);
  const safeStandardMax = Math.max(standardMaxSecs || observedTotalSecs, 1);

  // Build cumulative time + accuracy data (x = real elapsed time, not question index).
  // Standard-phase answers accumulate from 0; extra-time answers accumulate from the
  // standard-time boundary so they always sit inside the blue zone; corrections are
  // amendments to an earlier (standard-time) answer, so they are plotted at that
  // answer's position before the cutoff and consume no chart time.
  const data = [{ timeSecs: 0, rate: 0, inExtraTime: false, isCorrect: null }];
  let correctSoFar = 0;
  let stdTimeSecs = 0;
  let etTimeSecs = 0;
  answers.forEach((a, i) => {
    const isCorrection = isExtraTimeStudent && Boolean(a.changedFromIncorrectToCorrectInExtraTime);
    const isEtAnswer = isExtraTimeStudent && Boolean(a.isExtraTimePhase) && !isCorrection;
    let timeSecs;
    if (isCorrection) {
      timeSecs = stdTimeSecs;
    } else if (isEtAnswer) {
      etTimeSecs += Number(a.timeTaken) || 0;
      timeSecs = safeStandardMax + etTimeSecs;
    } else {
      stdTimeSecs += Number(a.timeTaken) || 0;
      timeSecs = stdTimeSecs;
    }
    if (a.isCorrect) correctSoFar++;
    data.push({
      timeSecs,
      rate: Math.round((correctSoFar / (i + 1)) * 100),
      inExtraTime: Boolean(a.isExtraTimePhase),
      isCorrect: a.isCorrect,
      changedFromIncorrectToCorrectInExtraTime: Boolean(a.changedFromIncorrectToCorrectInExtraTime),
    });
  });

  const totalTimeSecs = Math.max(...data.map((p) => p.timeSecs), 1);
  const finalRate = data[data.length - 1].rate;

  // Standard students show 0→standardMaxSecs, extra-time show 0→standardMaxSecs×1.25.
  // Combined with chartWidth of 80% vs 100%, pixels-per-second is identical across all charts.
  const allocatedMaxSecs = isExtraTimeStudent ? safeStandardMax * 1.25 : safeStandardMax;
  const domain = Math.max(allocatedMaxSecs, totalTimeSecs, 1);
  const chartWidth = isExtraTimeStudent ? '100%' : '80%';

  const width = 1000;
  const height = 200;
  const padding = 30;
  const innerW = width - 2 * padding;
  const innerH = height - 2 * padding;

  const xOf = (secs) => padding + (Math.min(Math.max(secs, 0), domain) / domain) * innerW;
  const yOf = (rate) => height - padding - (rate / 100) * innerH;

  const pathData = data.map((p, i) => `${i === 0 ? 'M' : 'L'} ${xOf(p.timeSecs)} ${yOf(p.rate)}`).join(' ');
  // Pin boundary to standardMaxSecs so all graphs align visually regardless of actual elapsed time
  const boundaryX = xOf(safeStandardMax);

  // X-axis ticks up to the plotted domain (allocated time or observed elapsed, whichever is larger)
  const domainMins = domain / 60;
  const tickIntervalMins = domainMins <= 5 ? 1 : domainMins <= 15 ? 2 : domainMins <= 30 ? 5 : 10;
  const timeTicks = [];
  for (let m = 0; m <= Math.ceil(domainMins); m += tickIntervalMins) timeTicks.push(m);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <span style={{ fontSize: '0.78rem', fontWeight: 900, color: BRAND.muted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Performance Tracker</span>
        <span style={{ fontSize: '0.9rem', fontWeight: 800, color: BRAND.teal, background: '#E0F2F1', padding: '4px 8px', borderRadius: 6 }}>
          Final Rate: {finalRate}%
        </span>
      </div>

      <svg width={chartWidth} viewBox={`0 0 ${width} ${height}`} style={{ overflow: 'visible', display: 'block' }}>
        {/* Extra time zone shading — only for extra time students */}
        {isExtraTimeStudent && (
          <rect x={boundaryX} y={padding} width={width - padding - boundaryX} height={innerH} fill="#EFF6FF" opacity={0.9} />
        )}

        {/* Y-axis gridlines (accuracy %) */}
        {[0, 25, 50, 75, 100].map(val => {
          const y = yOf(val);
          return (
            <g key={val}>
              <text x={padding - 10} y={y} textAnchor="end" dy="0.3em" fontSize="10" fill="#888" fontFamily="Inter">{val}%</text>
              <line x1={padding} y1={y} x2={width - padding} y2={y} stroke={val === 0 ? '#aaa' : '#f0f0f0'} strokeWidth="1" strokeDasharray={val > 0 ? '4' : '0'} />
            </g>
          );
        })}

        {/* X-axis time ticks (minutes) */}
        {timeTicks.map(m => {
          const x = xOf(m * 60);
          return (
            <g key={m}>
              <line x1={x} y1={height - padding} x2={x} y2={height - padding + 4} stroke="#aaa" strokeWidth="1" />
              <text x={x} y={height - padding + 15} textAnchor="middle" fontSize="9" fill="#888" fontFamily="Inter">{m}m</text>
            </g>
          );
        })}

        {/* Vertical boundary/end line — dashed blue for ET students, solid grey for standard */}
        {isExtraTimeStudent ? (
          <g>
            <line x1={boundaryX} y1={padding} x2={boundaryX} y2={height - padding} stroke="#3B82F6" strokeWidth="2" strokeDasharray="6 3" />
            <text x={boundaryX + 6} y={padding + 12} fontSize="9" fill="#3B82F6" fontWeight="800" fontFamily="Inter">EXTRA TIME →</text>
          </g>
        ) : (
          <line x1={width - padding} y1={padding} x2={width - padding} y2={height - padding} stroke="#94A3B8" strokeWidth="2" strokeDasharray="6 3" />
        )}

        {/* Main line */}
        <path d={pathData} fill="none" stroke={BRAND.teal} strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />

        {/* Dots — positioned at actual answer time on x-axis */}
        {data.slice(1).map((point, index) => {
          const x = xOf(point.timeSecs);
          const y = yOf(point.rate);
          const color = (point.changedFromIncorrectToCorrectInExtraTime && isExtraTimeStudent) ? '#3B82F6' :
                        (point.inExtraTime && isExtraTimeStudent) ? '#3B82F6' :
                        (point.isCorrect ? BRAND.teal : BRAND.coral);
          if (point.changedFromIncorrectToCorrectInExtraTime && isExtraTimeStudent) {
            return (
              <g key={index}>
                <circle cx={x} cy={y} r="11" fill="#DBEAFE" stroke="#3B82F6" strokeWidth="2" opacity="0.9" />
                <circle cx={x} cy={y} r="5" fill="#3B82F6" stroke={BRAND.charcoal} strokeWidth="1.5" />
                <text x={x} y={y - 15} textAnchor="middle" fontSize="9" fill="#3B82F6" fontWeight="800" fontFamily="Inter">Corrected</text>
              </g>
            );
          }
          return (
            <circle key={index} cx={x} cy={y} r="5" fill={color} stroke={BRAND.charcoal} strokeWidth="1.5" />
          );
        })}
      </svg>

      <div style={{ display: 'flex', gap: 14, marginTop: 8, flexWrap: 'wrap' }}>
        {[
          { color: BRAND.teal, label: 'Correct' },
          { color: BRAND.coral, label: 'Wrong' },
          ...(isExtraTimeStudent ? [{ color: '#3B82F6', label: 'Extra time zone' }] : []),
          ...(isExtraTimeStudent ? [{ color: '#DBEAFE', border: '#3B82F6', label: 'Corrected in Extra Time' }] : []),
        ].map(l => (
          <div key={l.label} style={{ alignItems: 'center', display: 'flex', gap: 5 }}>
            <div style={{ width: 12, height: 12, background: l.color, borderRadius: '50%', flexShrink: 0, border: l.border ? `2px solid ${l.border}` : 'none', boxSizing: 'border-box' }} />
            <span style={{ fontSize: '0.72rem', fontWeight: 700, color: BRAND.muted }}>{l.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Per-student marking row: shows the typed response or uploaded scan, plus an
// upload portal, mark input, and teacher comment that save to the writing attempt.
const WritingMarkingRow = ({ row, maxMarks, isWordProcessor }) => {
  const [mark, setMark] = useState(row.mark ?? '');
  const [comment, setComment] = useState(row.comment || '');
  const [uploadInfo, setUploadInfo] = useState({ url: row.response?.uploadUrl || '', filename: row.response?.uploadFilename || '' });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const typedText = row.response?.text || '';
  const extraTimeText = row.response?.extraTimeText || '';
  const wordCount = Number(row.response?.wordCount) || 0;
  const hasTypedResponse = Boolean(typedText || extraTimeText);
  const amendments = Array.isArray(row.response?.amendments)
    ? row.response.amendments.filter((a) => a && (a.original || a.amended))
    : [];

  const handleUpload = async (file) => {
    if (!file || !row.tutorialId) return;
    try {
      setUploading(true);
      setError('');
      setStatus('');
      const res = await jcqAPI.uploadExtendedWritingResponse(row.tutorialId, file);
      setUploadInfo({ url: res.data?.uploadUrl || '', filename: res.data?.uploadFilename || file.name });
      setStatus('Upload saved.');
    } catch (err) {
      setError('Upload failed. Please try again.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSaveMarking = async () => {
    if (!row.tutorialId) return;
    try {
      setSaving(true);
      setError('');
      setStatus('');
      await jcqAPI.saveExtendedWritingMarking(row.tutorialId, {
        mark: mark === '' ? null : Number(mark),
        comment
      });
      setStatus('Marking saved.');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not save marking.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ background: BRAND.white, border: `1.5px solid ${row.isTargetStudent ? '#93C5FD' : BRAND.border}`, borderRadius: 12, padding: 14 }}>
      <div style={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
        <strong style={{ color: BRAND.charcoal }}>{row.studentName}</strong>
        {row.isTargetStudent && <span style={{ background: '#DBEAFE', border: '1px solid #93C5FD', borderRadius: 999, color: '#1D4ED8', fontSize: '0.7rem', fontWeight: 900, padding: '2px 8px' }}>+25% EXTRA TIME</span>}
        <span style={{ background: isWordProcessor ? '#EAF6F7' : '#FFF4EC', border: `1px solid ${isWordProcessor ? BRAND.teal : BRAND.coral}`, borderRadius: 999, color: isWordProcessor ? '#0F766E' : '#B45309', fontSize: '0.7rem', fontWeight: 900, padding: '2px 8px' }}>
          {isWordProcessor ? 'WORD PROCESSOR' : 'PRINTED'}
        </span>
        {hasTypedResponse && <span style={{ color: BRAND.muted, fontSize: '0.75rem', fontWeight: 800, marginLeft: 'auto' }}>{wordCount} words</span>}
      </div>

      {hasTypedResponse ? (
        <div style={{ background: '#F9FAFB', border: `1.5px solid ${BRAND.border}`, borderRadius: 10, marginBottom: 12, overflow: 'hidden' }}>
          <div style={{ alignItems: 'center', background: BRAND.white, borderBottom: `1.5px solid ${BRAND.border}`, display: 'flex', flexWrap: 'wrap', gap: 12, padding: '8px 12px' }}>
            <span style={{ color: BRAND.muted, fontSize: '0.72rem', fontWeight: 900, letterSpacing: 0.5, textTransform: 'uppercase' }}>Typed response</span>
            {extraTimeText && (
              <span style={{ alignItems: 'center', display: 'inline-flex', fontSize: '0.72rem', fontWeight: 800, gap: 6 }}>
                <span style={{ background: '#DBEAFE', border: '1px solid #93C5FD', borderRadius: 4, display: 'inline-block', height: 12, width: 12 }} />
                <span style={{ color: '#1D4ED8' }}>Written in extra time</span>
              </span>
            )}
            {amendments.length > 0 && (
              <span style={{ alignItems: 'center', display: 'inline-flex', fontSize: '0.72rem', fontWeight: 800, gap: 6 }}>
                <span style={{ background: '#FEF3C7', border: '1px solid #FCD34D', borderRadius: 4, display: 'inline-block', height: 12, width: 12 }} />
                <span style={{ color: '#92400E' }}>{amendments.length} amendment{amendments.length === 1 ? '' : 's'}</span>
              </span>
            )}
          </div>
          <div style={{ color: BRAND.charcoal, lineHeight: 1.6, padding: 12, whiteSpace: 'pre-wrap' }}>
            {typedText}
            {extraTimeText && (
              <mark style={{ background: '#DBEAFE', borderRadius: 4, boxShadow: '0 0 0 2px #DBEAFE', color: '#1E3A8A', fontWeight: 700 }}>
                {typedText ? ` ${extraTimeText}` : extraTimeText}
              </mark>
            )}
          </div>
          {amendments.length > 0 && (
            <div style={{ background: '#FFFBEB', borderTop: '1.5px solid #FCD34D', padding: 12 }}>
              <div style={{ color: '#92400E', fontSize: '0.72rem', fontWeight: 900, letterSpacing: 0.5, marginBottom: 8, textTransform: 'uppercase' }}>Amendments made by student</div>
              <div style={{ display: 'grid', gap: 8 }}>
                {amendments.map((a, idx) => (
                  <div key={idx} style={{ background: BRAND.white, border: '1px solid #FCD34D', borderRadius: 8, fontSize: '0.85rem', lineHeight: 1.5, padding: '8px 10px' }}>
                    {a.original && <span style={{ color: '#9CA3AF', textDecoration: 'line-through' }}>{a.original}</span>}
                    {a.original && a.amended && <span style={{ color: BRAND.muted, fontWeight: 900, margin: '0 8px' }}>→</span>}
                    {a.amended && <span style={{ color: BRAND.charcoal, fontWeight: 800 }}>{a.amended}</span>}
                    {a.madeInExtraTime && (
                      <span style={{ background: '#DBEAFE', border: '1px solid #93C5FD', borderRadius: 999, color: '#1D4ED8', fontSize: '0.65rem', fontWeight: 900, marginLeft: 8, padding: '1px 7px', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                        EXTRA TIME
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div style={{ background: BRAND.cream, border: `1.5px dashed ${BRAND.border}`, borderRadius: 10, color: BRAND.muted, fontWeight: 800, marginBottom: 12, padding: 12 }}>
          {isWordProcessor ? 'No typed response submitted yet.' : 'Printed response — upload a scan or photo below.'}
        </div>
      )}

      <div style={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 12 }}>
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.png,.jpg,.jpeg,.webp,.heic"
          onChange={(e) => handleUpload(e.target.files?.[0])}
          disabled={uploading || !row.tutorialId}
          style={{ display: 'none' }}
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading || !row.tutorialId}
          style={{ background: uploading ? '#D8DEDF' : BRAND.charcoal, border: `2px solid ${BRAND.charcoal}`, borderRadius: 10, color: BRAND.white, cursor: uploading ? 'not-allowed' : 'pointer', fontWeight: 900, padding: '8px 14px' }}
        >
          {uploading ? 'Uploading…' : (uploadInfo.url ? 'Replace upload' : '⬆ Upload scan/photo')}
        </button>
        {uploadInfo.url && (
          <a href={uploadInfo.url} target="_blank" rel="noreferrer" style={{ color: BRAND.teal, fontWeight: 900, fontSize: '0.85rem' }}>
            View uploaded response{uploadInfo.filename ? ` (${uploadInfo.filename})` : ''} →
          </a>
        )}
      </div>

      <div style={{ alignItems: 'end', display: 'grid', gap: 12, gridTemplateColumns: 'auto 1fr auto' }}>
        <label style={{ color: BRAND.charcoal, fontWeight: 900, fontSize: '0.82rem' }}>
          Mark{maxMarks ? ` / ${maxMarks}` : ''}
          <input
            type="number"
            min={0}
            max={maxMarks || 100}
            step={0.5}
            value={mark}
            onChange={(e) => setMark(e.target.value)}
            style={{ border: `2px solid ${BRAND.charcoal}`, borderRadius: 10, boxSizing: 'border-box', color: BRAND.charcoal, display: 'block', fontWeight: 800, height: 44, marginTop: 6, padding: '0 12px', width: 100 }}
          />
        </label>
        <label style={{ color: BRAND.charcoal, fontWeight: 900, fontSize: '0.82rem', minWidth: 0 }}>
          Teacher comment
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Comments for the JCQ evidence record…"
            style={{ border: `2px solid ${BRAND.charcoal}`, borderRadius: 10, boxSizing: 'border-box', color: BRAND.charcoal, display: 'block', fontFamily: 'inherit', fontWeight: 700, height: 44, lineHeight: 1.4, marginTop: 6, minHeight: 44, padding: '9px 12px', resize: 'vertical', width: '100%' }}
          />
        </label>
        <button
          type="button"
          onClick={handleSaveMarking}
          disabled={saving || !row.tutorialId}
          style={{ background: saving ? '#D8DEDF' : BRAND.teal, border: `2px solid ${BRAND.charcoal}`, borderRadius: 10, boxSizing: 'border-box', color: saving ? BRAND.muted : BRAND.white, cursor: saving ? 'not-allowed' : 'pointer', fontWeight: 950, height: 44, padding: '0 18px', whiteSpace: 'nowrap' }}
        >
          {saving ? 'Saving…' : 'Save marking'}
        </button>
      </div>

      {error && <div style={{ color: '#8A2C1D', fontWeight: 800, marginTop: 8 }}>{error}</div>}
      {status && <div style={{ color: BRAND.success, fontWeight: 800, marginTop: 8 }}>{status}</div>}
    </div>
  );
};

const ExtendedWritingEvidenceSection = ({ report = {}, students = [] }) => {
  const prompt = report.extendedWritingPrompt || null;
  const wordProcessorStudentIds = useMemo(() => (
    new Set((Array.isArray(report.wordProcessorStudentIds) ? report.wordProcessorStudentIds : []).map(String))
  ), [report.wordProcessorStudentIds]);

  const rows = students
    .filter((student) => student.extendedWriting)
    .map((student) => ({
      studentId: student.studentId,
      studentName: student.studentName,
      isTargetStudent: student.isTargetStudent,
      tutorialId: student.extendedWriting?.tutorialId || null,
      response: student.extendedWriting?.response || null,
      mark: student.extendedWriting?.mark ?? null,
      comment: student.extendedWriting?.comment || null
    }));
  const savedMarks = Number.isFinite(Number(prompt?.marks)) ? Number(prompt.marks) : null;

  return (
    <div style={{ marginTop: 30, paddingTop: 24, borderTop: '2px dashed rgba(45,64,66,0.18)' }}>
      <h3 style={{ color: BRAND.charcoal, fontWeight: 900, fontSize: '1rem', margin: '0 0 4px' }}>
        4 · Extended Writing Test — Marking Portal
      </h3>
      <p style={{ color: BRAND.muted, fontSize: '0.82rem', fontWeight: 700, margin: '0 0 14px' }}>
        Typed responses from word-processor students appear automatically; upload scans or photos for students who sat the printed version. Add a mark and comment for each response for Form 8 review.
      </p>

      <div style={{ background: '#F8FAFC', border: `2px solid ${BRAND.border}`, borderRadius: 14, padding: 16, marginBottom: 16 }}>
        <div style={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 10, justifyContent: 'space-between', marginBottom: 8 }}>
          <strong style={{ color: BRAND.charcoal, fontFamily: 'Nunito, sans-serif', fontSize: '1rem' }}>Writing Test Question</strong>
          <div style={{ alignItems: 'center', display: 'flex', gap: 8 }}>
            {savedMarks && <span style={{ background: BRAND.coral, borderRadius: 999, color: BRAND.white, fontSize: '0.75rem', fontWeight: 900, padding: '4px 10px' }}>{savedMarks} marks</span>}
            {Number.isFinite(Number(prompt?.durationMinutes)) && (
              <span style={{ background: BRAND.teal, borderRadius: 999, color: BRAND.white, fontSize: '0.75rem', fontWeight: 900, padding: '4px 10px' }}>
                {Number(prompt.durationMinutes)} min · {Math.round(Number(prompt.durationMinutes) * 1.25 * 100) / 100} min extra time
              </span>
            )}
          </div>
        </div>
        <div style={{ color: prompt?.question ? BRAND.charcoal : BRAND.muted, fontWeight: prompt?.question ? 800 : 700, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
          {prompt?.question || 'No extended writing question has been published yet. Build one in Step 4.1 and publish the test.'}
        </div>
        {prompt?.sourceSubject && <div style={{ color: BRAND.muted, fontSize: '0.78rem', fontWeight: 800, marginTop: 8 }}>Source subject: {prompt.sourceSubject}</div>}
      </div>

      {rows.length === 0 ? (
        <div style={{ background: BRAND.cream, border: `2px dashed ${BRAND.border}`, borderRadius: 12, color: BRAND.muted, fontWeight: 800, padding: 14 }}>
          Student marking rows will appear here once a writing test has been published for this class.
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 12 }}>
          {rows.map((row) => (
            <WritingMarkingRow
              key={row.studentId}
              row={row}
              maxMarks={savedMarks}
              isWordProcessor={wordProcessorStudentIds.has(String(row.studentId))}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Step 4.1 — Extended Writing Test builder. Generates/saves the writing question
// locally; the standalone writing test is created when the teacher publishes in Step 5.
const ExtendedWritingTestBuilder = ({ bucketASelection, savedPrompt, onSave, onClear }) => {
  const [questionType, setQuestionType] = useState(savedPrompt?.questionType || 'short');
  const [durationMinutes, setDurationMinutes] = useState(
    Number.isFinite(Number(savedPrompt?.durationMinutes))
      ? Number(savedPrompt.durationMinutes)
      : (savedPrompt?.questionType === 'long' ? 15 : 5)
  );
  const [questions, setQuestions] = useState([]);
  const [selectedQuestionId, setSelectedQuestionId] = useState('');
  const [customQuestion, setCustomQuestion] = useState('');
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');

  const marks = questionType === 'long' ? 15 : 5;
  const extraTimeMinutes = Math.round(durationMinutes * 1.25 * 100) / 100;
  const selectedQuestion = questions.find((q) => String(q.id) === String(selectedQuestionId)) || null;
  const finalQuestionText = customQuestion.trim() || selectedQuestion?.question || '';

  const handleGenerate = async () => {
    if (!bucketASelection?.id) {
      setError('Select a Bucket A (Extended Writing) lesson in Step 3 first.');
      return;
    }
    try {
      setGenerating(true);
      setError('');
      setQuestions([]);
      setSelectedQuestionId('');
      const response = await jcqAPI.generateExtendedWritingQuestions({
        subject: bucketASelection.subject || '',
        questionType,
        yearGroup: bucketASelection.yearGroup || 'Year 11',
        lessonPlanId: bucketASelection.id,
        previewHomeworkId: bucketASelection.previewHomeworkId || bucketASelection.homeworkId || ''
      });
      const generated = Array.isArray(response.data?.questions) ? response.data.questions : [];
      setQuestions(generated);
      setSelectedQuestionId(generated[0]?.id || '');
      setCustomQuestion('');
      if (!generated.length) setError('No questions were generated. Please try again.');
    } catch (err) {
      setError('Could not generate questions. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = () => {
    if (!finalQuestionText) {
      setError('Choose a generated question or write a custom one first.');
      return;
    }
    setError('');
    onSave({ question: finalQuestionText, questionType, marks, durationMinutes, sourceSubject: bucketASelection?.subject || '' });
  };

  if (!bucketASelection) {
    return (
      <div style={{ background: BRAND.cream, border: `2px dashed ${BRAND.border}`, borderRadius: 14, color: BRAND.muted, fontWeight: 800, padding: 18 }}>
        Select a Bucket A (Extended Writing) lesson in Step 3 to build the writing test here.
      </div>
    );
  }

  return (
    <div>
      <div style={{ background: '#F8FAFC', border: `2px solid ${BRAND.border}`, borderRadius: 14, marginBottom: 16, padding: 16 }}>
        <div style={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 12 }}>
          <strong style={{ color: BRAND.charcoal }}>Generate questions from: {bucketASelection.subject || 'Bucket A lesson'}</strong>
        </div>
        <div style={{ alignItems: 'end', display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', marginBottom: 4 }}>
          <label style={{ color: BRAND.charcoal, fontWeight: 900 }}>
            Question length
            <select
              value={questionType}
              onChange={(e) => {
                const nextType = e.target.value;
                setQuestionType(nextType);
                setDurationMinutes(nextType === 'long' ? 15 : 5);
                setQuestions([]);
                setSelectedQuestionId('');
              }}
              style={{ border: `2px solid ${BRAND.charcoal}`, borderRadius: 10, boxSizing: 'border-box', color: BRAND.charcoal, display: 'block', fontWeight: 800, height: 46, marginTop: 6, padding: '0 12px', width: '100%' }}
            >
              <option value="short">Short · 5 marks</option>
              <option value="long">Long · 15 marks</option>
            </select>
          </label>
          <label style={{ color: BRAND.charcoal, fontWeight: 900 }}>
            Time length
            <select
              value={String(durationMinutes)}
              onChange={(e) => setDurationMinutes(Number(e.target.value))}
              style={{ border: `2px solid ${BRAND.charcoal}`, borderRadius: 10, boxSizing: 'border-box', color: BRAND.charcoal, display: 'block', fontWeight: 800, height: 46, marginTop: 6, padding: '0 12px', width: '100%' }}
            >
              {[5, 10, 15, 20, 25, 30].map((mins) => (
                <option key={mins} value={String(mins)}>{mins} minutes</option>
              ))}
            </select>
          </label>
          <button
            type="button"
            onClick={handleGenerate}
            disabled={generating}
            style={{ background: generating ? '#D8DEDF' : BRAND.coral, border: `2px solid ${BRAND.charcoal}`, borderRadius: 12, boxSizing: 'border-box', color: generating ? BRAND.muted : BRAND.white, cursor: generating ? 'not-allowed' : 'pointer', fontWeight: 950, height: 46, padding: '0 16px', width: '100%' }}
          >
            {generating ? 'Generating…' : `Generate 3 × ${marks}-mark questions`}
          </button>
        </div>
        <div style={{ color: BRAND.muted, fontSize: '0.75rem', fontWeight: 800, marginBottom: 12 }}>
          Extra-time students get +25% ({extraTimeMinutes} min)
        </div>

        {error && (
          <div style={{ background: '#FFF0F0', border: `2px solid ${BRAND.danger}`, borderRadius: 10, color: '#8A2C1D', fontWeight: 800, marginBottom: 10, padding: 12 }}>
            {error}
          </div>
        )}

        {questions.length > 0 && (
          <div style={{ display: 'grid', gap: 10, marginBottom: 12 }}>
            {questions.map((q, idx) => {
              const id = q.id || `Q${idx + 1}`;
              const selected = String(selectedQuestionId) === String(id) && !customQuestion.trim();
              return (
                <label key={id} style={{ background: selected ? '#EAF6F7' : BRAND.white, border: `2px solid ${selected ? BRAND.teal : BRAND.border}`, borderRadius: 12, cursor: 'pointer', display: 'block', padding: 14 }}>
                  <input
                    type="radio"
                    name="ew-builder-question"
                    checked={selected}
                    onChange={() => { setSelectedQuestionId(id); setCustomQuestion(''); }}
                    style={{ marginRight: 10 }}
                  />
                  <strong style={{ color: BRAND.charcoal }}>{q.title || `Option ${idx + 1}`}</strong>
                  <p style={{ color: BRAND.charcoal, fontWeight: 800, lineHeight: 1.45, margin: '8px 0 0' }}>{q.question}</p>
                </label>
              );
            })}
          </div>
        )}

        <label style={{ color: BRAND.charcoal, display: 'block', fontWeight: 900, marginBottom: 12 }}>
          Or write your own {marks}-mark question
          <textarea
            value={customQuestion}
            onChange={(e) => setCustomQuestion(e.target.value)}
            placeholder="Type a custom extended writing question here…"
            rows={3}
            style={{ border: `2px solid ${BRAND.charcoal}`, borderRadius: 10, boxSizing: 'border-box', color: BRAND.charcoal, display: 'block', fontWeight: 700, lineHeight: 1.45, marginTop: 6, padding: 12, resize: 'vertical', width: '100%' }}
          />
        </label>

        <button
          type="button"
          onClick={handleSave}
          disabled={!finalQuestionText}
          style={{ background: !finalQuestionText ? '#D8DEDF' : BRAND.teal, border: `2px solid ${BRAND.charcoal}`, borderRadius: 12, color: !finalQuestionText ? BRAND.muted : BRAND.white, cursor: !finalQuestionText ? 'not-allowed' : 'pointer', fontWeight: 950, padding: '12px 20px' }}
        >
          Save question to writing test
        </button>
      </div>

      <div style={{ background: savedPrompt?.question ? '#EAF7EA' : '#F8FAFC', border: `2px solid ${savedPrompt?.question ? BRAND.success : BRAND.border}`, borderRadius: 14, padding: 16 }}>
        <div style={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 10, justifyContent: 'space-between', marginBottom: 8 }}>
          <strong style={{ color: BRAND.charcoal, fontFamily: 'Nunito, sans-serif', fontSize: '1rem' }}>Saved Writing Test Question</strong>
          <div style={{ alignItems: 'center', display: 'flex', gap: 10 }}>
            {Number.isFinite(Number(savedPrompt?.marks)) && (
              <span style={{ background: BRAND.coral, borderRadius: 999, color: BRAND.white, fontSize: '0.75rem', fontWeight: 900, padding: '4px 10px' }}>{Number(savedPrompt.marks)} marks</span>
            )}
            {Number.isFinite(Number(savedPrompt?.durationMinutes)) && (
              <span style={{ background: BRAND.teal, borderRadius: 999, color: BRAND.white, fontSize: '0.75rem', fontWeight: 900, padding: '4px 10px' }}>
                {Number(savedPrompt.durationMinutes)} min · {Math.round(Number(savedPrompt.durationMinutes) * 1.25 * 100) / 100} min extra time
              </span>
            )}
            {savedPrompt?.question && (
              <button
                type="button"
                onClick={onClear}
                style={{ background: 'none', border: 'none', color: BRAND.muted, cursor: 'pointer', fontSize: '0.8rem', fontWeight: 800, padding: 0, textDecoration: 'underline' }}
              >
                Remove question
              </button>
            )}
          </div>
        </div>
        <div style={{ color: savedPrompt?.question ? BRAND.charcoal : BRAND.muted, fontWeight: savedPrompt?.question ? 800 : 700, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
          {savedPrompt?.question || 'No writing test question saved yet. It will be published as a standalone writing test alongside Buckets A, B and C.'}
        </div>
        {savedPrompt?.sourceSubject && <div style={{ color: BRAND.muted, fontSize: '0.78rem', fontWeight: 800, marginTop: 8 }}>Source subject: {savedPrompt.sourceSubject}</div>}
      </div>
    </div>
  );
};

const JCQReport = ({ classId, testLinksAvailable }) => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filterTarget, setFilterTarget] = useState(false);
  const [selectedBucketId, setSelectedBucketId] = useState('BUCKET_A');

  useEffect(() => {
    if (!classId || !testLinksAvailable) { setReport(null); return; }
    let mounted = true;
    setLoading(true);
    jcqAPI.getExtraTimeReport(classId)
      .then(res => { if (mounted) setReport(res.data); })
      .catch(() => {})
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [classId, testLinksAvailable]);

  if (!testLinksAvailable) return null;
  if (loading) return (
    <section style={{ ...cardStyle, marginBottom: 24 }}>
      <span style={stepBadgeStyle}>JCQ Evidence Report</span>
      <p style={{ color: BRAND.muted, fontWeight: 800, marginTop: 14 }}>Loading report…</p>
    </section>
  );
  if (!report || !Array.isArray(report.subjects) || report.subjects.length === 0) return null;

  const { subjects, students = [] } = report;
  const visibleStudents = filterTarget ? students.filter(s => s.isTargetStudent) : students;
  const hasTargetStudents = students.some(s => s.isTargetStudent);

  const selectedSubject = subjects.find(s => s.bucketId === selectedBucketId) || subjects[0];

  // Bucket-specific processing speed stats for Section 3
  const bucketAvg = (student) => student.buckets[selectedBucketId]?.averageTimeSecs || 0;
  const bucketStudentsWithData = visibleStudents.filter(s => s.buckets[selectedBucketId]?.averageTimeSecs != null);
  const bucketClassAvgSecs = bucketStudentsWithData.length > 0
    ? Math.round(bucketStudentsWithData.reduce((s, st) => s + bucketAvg(st), 0) / bucketStudentsWithData.length)
    : null;
  const bucketTargetStudents = bucketStudentsWithData.filter(s => s.isTargetStudent);
  const bucketTargetAvgSecs = bucketTargetStudents.length > 0
    ? Math.round(bucketTargetStudents.reduce((s, st) => s + bucketAvg(st), 0) / bucketTargetStudents.length)
    : null;
  const bucketMaxAvg = Math.max(...bucketStudentsWithData.map(bucketAvg), 0);
  const bucketTimeDeficit = bucketClassAvgSecs && bucketTargetAvgSecs
    ? Math.round(((bucketTargetAvgSecs - bucketClassAvgSecs) / bucketClassAvgSecs) * 100)
    : null;

  const handleBucketCardClick = (bucketId) => {
    setSelectedBucketId(prev => prev === bucketId ? null : bucketId);
  };

  return (
    <section style={{ ...cardStyle, marginBottom: 24 }}>
      {/* Header */}
      <div style={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'space-between', marginBottom: 24 }}>
        <span style={stepBadgeStyle}>JCQ Evidence Report</span>
        {hasTargetStudents && (
          <button
            type="button"
            onClick={() => setFilterTarget(f => !f)}
            style={{ background: filterTarget ? BRAND.teal : BRAND.white, border: `2px solid ${BRAND.charcoal}`, borderRadius: 10, color: filterTarget ? BRAND.white : BRAND.charcoal, cursor: 'pointer', fontWeight: 900, fontSize: '0.82rem', padding: '6px 14px' }}
          >
            {filterTarget ? 'Showing: Extra Time Students' : 'Show: All Students'}
          </button>
        )}
      </div>

      {/* Section 1 — 3-Subject Spread */}
      <div style={{ marginBottom: 28 }}>
        <h3 style={{ color: BRAND.charcoal, fontWeight: 900, fontSize: '1rem', margin: '0 0 4px' }}>1 · Subject Spread (Compliance Check)</h3>
        <p style={{ color: BRAND.muted, fontSize: '0.82rem', fontWeight: 700, margin: '0 0 14px' }}>
          JCQ requires evidence across Extended Writing, STEM, and Applied — confirming a generalised processing speed difficulty.
          {selectedBucketId ? ' Click the selected subject again to deselect.' : ' Click a subject to focus the analysis below.'}
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
          {subjects.map(sub => {
            const col = BUCKET_COLORS[sub.bucketId] || BUCKET_COLORS.BUCKET_A;
            const isSelected = selectedBucketId === sub.bucketId;
            return (
              <button
                key={sub.bucketId}
                type="button"
                onClick={() => handleBucketCardClick(sub.bucketId)}
                style={{
                  background: col.bg,
                  border: isSelected ? `3px solid ${col.text}` : `2px solid ${col.border}`,
                  borderRadius: 14,
                  cursor: 'pointer',
                  padding: '14px 16px',
                  textAlign: 'left',
                  outline: 'none',
                  boxShadow: isSelected ? `0 0 0 3px ${col.border}` : 'none',
                  transform: isSelected ? 'scale(1.02)' : 'scale(1)',
                  transition: 'transform 0.15s, box-shadow 0.15s, border 0.15s',
                }}
              >
                <div style={{ color: col.text, fontWeight: 900, fontSize: '1.4rem', lineHeight: 1 }}>
                  {sub.bucketLetter}
                </div>
                <div style={{ color: BRAND.charcoal, fontWeight: 900, fontSize: '0.95rem', marginTop: 6 }}>{sub.subject}</div>
                <div style={{ color: col.text, fontSize: '0.75rem', fontWeight: 800, marginTop: 3, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{sub.label}</div>
                <div style={{ alignItems: 'center', background: isSelected ? col.text : col.border, borderRadius: 20, color: isSelected ? BRAND.white : col.text, display: 'inline-flex', fontSize: '0.72rem', fontWeight: 900, gap: 4, marginTop: 8, padding: '3px 10px' }}>
                  {isSelected ? '▶ Selected' : '✓ Compliant'}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Section 2 — Per-student analysis */}
      <div style={{ marginBottom: 28 }}>
        <h3 style={{ color: BRAND.charcoal, fontWeight: 900, fontSize: '1rem', margin: '0 0 4px' }}>
          2 · {selectedSubject?.bucketLetter} · {selectedSubject?.subject} — Per-Student Question Analysis
        </h3>
        <p style={{ color: BRAND.muted, fontSize: '0.82rem', fontWeight: 700, margin: '0 0 14px' }}>
          Cumulative accuracy plotted against real time (x-axis = minutes elapsed). Each dot = one question answered. Extra time students show a blue-shaded zone beyond the standard time boundary.
        </p>
        <div style={{ display: 'grid', gap: 16 }}>
          {(() => {
            const standardMaxSecs = selectedSubject?.standardDurationSecs || report.standardDurationSecs || 900;
            return visibleStudents.map(student => {
              const bucketStats = student.buckets[selectedBucketId];
              return (
                <div
                  key={student.studentId}
                  style={{ background: BRAND.white, border: `1.5px solid ${student.isTargetStudent ? '#93C5FD' : BRAND.border}`, borderRadius: 12, padding: '14px 16px' }}
                >
                  <div style={{ alignItems: 'center', display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                    <span style={{ color: BRAND.charcoal, fontWeight: 900, fontSize: '0.9rem' }}>{student.studentName}</span>
                    {student.isTargetStudent && (
                      <span style={{ background: '#DBEAFE', border: '1px solid #93C5FD', borderRadius: '999px', color: '#1D4ED8', fontSize: '0.7rem', fontWeight: 900, padding: '2px 8px' }}>+25% EXTRA TIME</span>
                    )}
                    {bucketStats?.averageTimeSecs != null && (
                      <span style={{ color: BRAND.muted, fontSize: '0.72rem', fontWeight: 700, marginLeft: 'auto' }}>
                        {bucketStats.averageTimeSecs}s avg/q
                      </span>
                    )}
                  </div>
                  <JCQPerformanceTrackerChart stats={bucketStats} isExtraTimeStudent={student.isTargetStudent} standardMaxSecs={standardMaxSecs} />
                  {bucketStats?.tutorialId && (
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
                      <Link
                        to={`/tutorial-report/${bucketStats.tutorialId}?jcq=1${student.isTargetStudent ? '&et=1' : ''}&stdmax=${Math.round(standardMaxSecs)}`}
                        style={{ background: BRAND.teal, borderRadius: 8, color: BRAND.white, fontSize: '0.75rem', fontWeight: 900, padding: '5px 14px', textDecoration: 'none' }}
                      >
                        View Report →
                      </Link>
                    </div>
                  )}
                </div>
              );
            });
          })()}
        </div>
      </div>

      {/* Section 3 — Cohort Comparison */}
      <div>
        <h3 style={{ color: BRAND.charcoal, fontWeight: 900, fontSize: '1rem', margin: '0 0 4px' }}>
          3 · {selectedSubject?.bucketLetter} · {selectedSubject?.subject} — Processing Speed vs Class (Proof of Deficit)
        </h3>
        <p style={{ color: BRAND.muted, fontSize: '0.82rem', fontWeight: 700, margin: '0 0 6px' }}>
          Average seconds per question for this subject. Target students should show a measurable deficit against the class average.
        </p>
        {bucketTimeDeficit !== null && (
          <div style={{ background: '#EFF6FF', border: '2px solid #93C5FD', borderRadius: 12, display: 'inline-flex', gap: 20, flexWrap: 'wrap', padding: '10px 18px', marginBottom: 16 }}>
            <span style={{ color: '#1D4ED8', fontWeight: 900 }}>
              Extra time students avg: <strong>{bucketTargetAvgSecs}s/q</strong>
            </span>
            <span style={{ color: BRAND.muted, fontWeight: 900 }}>
              Class avg: <strong>{bucketClassAvgSecs}s/q</strong>
            </span>
            <span style={{ color: bucketTimeDeficit > 0 ? '#DC2626' : '#15803D', fontWeight: 900 }}>
              {bucketTimeDeficit > 0 ? `+${bucketTimeDeficit}% slower` : `${Math.abs(bucketTimeDeficit)}% faster`}
            </span>
          </div>
        )}
        <div style={{ display: 'grid', gap: 6, position: 'relative' }}>
          {bucketClassAvgSecs && bucketMaxAvg > 0 && (
            <div style={{ position: 'absolute', top: 0, bottom: 0, left: `calc(${(bucketClassAvgSecs / bucketMaxAvg) * 100}% + 4px)`, width: 2, background: BRAND.coral, zIndex: 1, pointerEvents: 'none' }}>
              <span style={{ background: BRAND.coral, borderRadius: 4, color: BRAND.white, fontSize: '0.65rem', fontWeight: 900, padding: '2px 5px', position: 'absolute', top: 0, left: 4, whiteSpace: 'nowrap' }}>
                Class avg {bucketClassAvgSecs}s
              </span>
            </div>
          )}
          {bucketStudentsWithData
            .sort((a, b) => bucketAvg(b) - bucketAvg(a))
            .map(student => {
              const pct = bucketMaxAvg > 0 ? (bucketAvg(student) / bucketMaxAvg) * 100 : 0;
              const isTarget = student.isTargetStudent;
              return (
                <div key={student.studentId} style={{ alignItems: 'center', display: 'flex', gap: 10 }}>
                  <div style={{ color: isTarget ? '#1D4ED8' : BRAND.charcoal, fontSize: '0.8rem', fontWeight: isTarget ? 900 : 700, minWidth: 120, textAlign: 'right' }}>
                    {student.studentName}
                  </div>
                  <div style={{ flex: 1, position: 'relative' }}>
                    <div style={{ background: isTarget ? '#3B82F6' : '#D1D5DB', borderRadius: 4, height: 22, width: `${pct}%`, minWidth: 4, transition: 'width 0.4s', display: 'flex', alignItems: 'center', paddingLeft: 8 }}>
                      <span style={{ color: isTarget ? BRAND.white : BRAND.charcoal, fontSize: '0.72rem', fontWeight: 900, whiteSpace: 'nowrap' }}>
                        {bucketAvg(student)}s
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
        </div>
        <div style={{ display: 'flex', gap: 16, marginTop: 12, flexWrap: 'wrap' }}>
          <div style={{ alignItems: 'center', display: 'flex', gap: 6 }}>
            <div style={{ width: 14, height: 14, background: '#3B82F6', borderRadius: 3 }} />
            <span style={{ fontSize: '0.75rem', fontWeight: 700, color: BRAND.muted }}>Extra time student</span>
          </div>
          <div style={{ alignItems: 'center', display: 'flex', gap: 6 }}>
            <div style={{ width: 14, height: 14, background: '#D1D5DB', borderRadius: 3 }} />
            <span style={{ fontSize: '0.75rem', fontWeight: 700, color: BRAND.muted }}>Standard student</span>
          </div>
          <div style={{ alignItems: 'center', display: 'flex', gap: 6 }}>
            <div style={{ width: 14, height: 3, background: BRAND.coral, borderRadius: 2, marginTop: 1 }} />
            <span style={{ fontSize: '0.75rem', fontWeight: 700, color: BRAND.muted }}>Class average</span>
          </div>
        </div>
      </div>

      <ExtendedWritingEvidenceSection report={report} students={visibleStudents} />
    </section>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

const JCQExtraTimeTest = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const initialFormStateRef = useRef(loadSavedJcqFormState());
  const [selectedClassId, setSelectedClassId] = useState(() => initialFormStateRef.current.selectedClassId || '');
  const [selectedByBucket, setSelectedByBucket] = useState(() => initialFormStateRef.current.selectedByBucket || {});
  const [targetStudentIds, setTargetStudentIds] = useState(() => (
    Array.isArray(initialFormStateRef.current.targetStudentIds) ? initialFormStateRef.current.targetStudentIds : []
  ));
  const [wordProcessorStudentIds, setWordProcessorStudentIds] = useState(() => (
    Array.isArray(initialFormStateRef.current.wordProcessorStudentIds) ? initialFormStateRef.current.wordProcessorStudentIds : []
  ));
  const [extendedWritingPrompt, setExtendedWritingPrompt] = useState(() => initialFormStateRef.current.extendedWritingPrompt || null);
  const [publishedExtendedWritingPrompt, setPublishedExtendedWritingPrompt] = useState(null);
  const [activeBuildBucketId, setActiveBuildBucketId] = useState(() => initialFormStateRef.current.activeBuildBucketId || JCQ_BUCKETS[0].id);
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [scannedBucketIds, setScannedBucketIds] = useState(() => initialFormStateRef.current.scannedBucketIds || []);
  const [approvedByStudent, setApprovedByStudent] = useState(() => initialFormStateRef.current.approvedByStudent || {});
  const [publishedTestLink, setPublishedTestLink] = useState(() => initialFormStateRef.current.publishedTestLink || '');
  const [publishedLinksByStudent, setPublishedLinksByStudent] = useState({});
  const [publishedBucketAssignments, setPublishedBucketAssignments] = useState([]);
  const [publishedLinksLoading, setPublishedLinksLoading] = useState(false);
  const [linksRefreshKey, setLinksRefreshKey] = useState(0);
  const [copiedKey, setCopiedKey] = useState(null);
  const [bucketPreviewTutorials, setBucketPreviewTutorials] = useState({});
  const [bucketPreviewSlideshows, setBucketPreviewSlideshows] = useState({});
  const [approvalModal, setApprovalModal] = useState({ isOpen: false, studentId: '', bucketId: '', tutorial: null, studentName: '' });
  const [approvalModalScrolled, setApprovalModalScrolled] = useState(false);
  const approvalScrollRef = useRef(null);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Handle returning from content library with a selection
  useEffect(() => {
    const stateData = location.state || {};
    const incomingBucketId = stateData.jcqReturnBucketId;
    const incomingLessonPlan = stateData.jcqLessonPlan;

    if (incomingBucketId && incomingLessonPlan) {
      setSelectedByBucket((prev) => ({ ...prev, [incomingBucketId]: incomingLessonPlan }));
      setActiveBuildBucketId(incomingBucketId);
    }

    if (incomingBucketId) {
      navigate(location.pathname, { replace: true, state: {} });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-save all form state to localStorage on every change so it persists
  // across navigation, reloads and logouts until the class changes or
  // selections are cleared.
  useEffect(() => {
    localStorage.setItem(getJcqFormStorageKey(), JSON.stringify({ selectedClassId, targetStudentIds, wordProcessorStudentIds, extendedWritingPrompt, selectedByBucket, activeBuildBucketId, scannedBucketIds, approvedByStudent, publishedTestLink }));
  }, [selectedClassId, targetStudentIds, wordProcessorStudentIds, extendedWritingPrompt, selectedByBucket, activeBuildBucketId, scannedBucketIds, approvedByStudent, publishedTestLink]);

  useEffect(() => {
    const selectedStudentIds = new Set(students.map((student) => normalizeId(student)).filter(Boolean));
    setApprovedByStudent((previous) => Object.fromEntries(
      Object.entries(previous).filter(([studentId]) => selectedStudentIds.has(studentId))
    ));
  }, [students]);

  useEffect(() => {
    setPublishedTestLink('');
  }, [selectedClassId, selectedByBucket, targetStudentIds]);

  useEffect(() => {
    setApprovedByStudent({});
  }, [selectedClassId, selectedByBucket]);

  useEffect(() => {
    setScannedBucketIds((previous) => previous.filter((bucketId) => selectedByBucket[bucketId]));
  }, [selectedByBucket]);

  useEffect(() => {
    let isMounted = true;
    const loadBucketPreviewTutorials = async () => {
      const entries = await Promise.all(JCQ_BUCKETS.map(async (bucket) => {
        const homeworkId = selectedByBucket[bucket.id]?.previewHomeworkId || selectedByBucket[bucket.id]?.homeworkId;
        if (!homeworkId) return [bucket.id, null];
        try {
          const res = await studentTutorialAPI.getForHomework(homeworkId);
          const tutorials = Array.isArray(res.data) ? res.data : [];
          const completed = tutorials.find((tutorial) => tutorial.status === 'COMPLETED' && tutorial.generatedContent?.length > 0) || tutorials[0] || null;
          return [bucket.id, completed];
        } catch (err) {
          console.warn(`Failed to load preview tutorial for ${bucket.id}:`, err);
          return [bucket.id, null];
        }
      }));
      if (isMounted) setBucketPreviewTutorials(Object.fromEntries(entries));
    };

    loadBucketPreviewTutorials();
    return () => { isMounted = false; };
  }, [selectedByBucket]);

  useEffect(() => {
    if (approvalModal.isOpen) setApprovalModalScrolled(false);
  }, [approvalModal.isOpen, approvalModal.bucketId, approvalModal.studentId]);

  useEffect(() => {
    let isMounted = true;

    const fetchInitialData = async () => {
      try {
        setInitialLoading(true);
        setError('');

        const classesRes = await studentAPI.getClasses(true);

        if (!isMounted) return;

        setClasses(Array.isArray(classesRes.data) ? classesRes.data : []);
      } catch (err) {
        console.error('Failed to load JCQ test setup data:', err);
        if (isMounted) {
          setError('We could not load your classes. Please refresh and try again.');
        }
      } finally {
        if (isMounted) setInitialLoading(false);
      }
    };

    fetchInitialData();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const fetchStudentsForClass = async () => {
      if (!selectedClassId) {
        setStudents([]);
        return;
      }

      try {
        setStudentsLoading(true);
        setError('');
        const studentsRes = await studentAPI.getForSchedule(selectedClassId);

        if (!isMounted) return;

        const loadedStudents = Array.isArray(studentsRes.data) ? studentsRes.data : [];
        const loadedStudentIds = new Set(loadedStudents.map((student) => normalizeId(student)).filter(Boolean));
        setStudents(loadedStudents);
        setTargetStudentIds((previous) => previous.filter((studentId) => loadedStudentIds.has(String(studentId))));
        setWordProcessorStudentIds((previous) => previous.filter((studentId) => loadedStudentIds.has(String(studentId))));
      } catch (err) {
        console.error('Failed to load students for JCQ class:', err);
        if (isMounted) {
          setStudents([]);
          setTargetStudentIds([]);
          setWordProcessorStudentIds([]);
          setError('We could not load the students for that class. Please choose the class again or try later.');
        }
      } finally {
        if (isMounted) setStudentsLoading(false);
      }
    };

    fetchStudentsForClass();

    return () => {
      isMounted = false;
    };
  }, [selectedClassId]);

  useEffect(() => {
    let isMounted = true;

    const fetchPublishedLinks = async () => {
      if (!selectedClassId) {
        setPublishedLinksByStudent({});
        setPublishedBucketAssignments([]);
        setPublishedTestLink('');
        return;
      }

      try {
        setPublishedLinksLoading(true);
        const response = await jcqAPI.getExtraTimeTests(selectedClassId);
        if (!isMounted) return;
        const linksByStudent = response.data?.linksByStudent || {};
        setPublishedLinksByStudent(linksByStudent);
        setPublishedBucketAssignments(Array.isArray(response.data?.bucketAssignments) ? response.data.bucketAssignments : []);
        setPublishedExtendedWritingPrompt(response.data?.extendedWritingPrompt || null);
        if (hasAnyPublishedBucketLink(linksByStudent)) {
          setPublishedTestLink('individual-links');
        } else {
          setPublishedTestLink('');
        }
      } catch (err) {
        console.error('Failed to fetch published JCQ links:', err);
        // Do not wipe existing link state on GET failure — POST response may have already populated it
      } finally {
        if (isMounted) setPublishedLinksLoading(false);
      }
    };

    fetchPublishedLinks();
    return () => { isMounted = false; };
  }, [selectedClassId, linksRefreshKey]);

  const selectedBucketCount = Object.keys(selectedByBucket).length;
  const publishStudents = students;
  const hasFullyApprovedStudent = publishStudents.some((student) => {
    const approvals = approvedByStudent[normalizeId(student)] || {};
    return JCQ_BUCKETS.every((bucket) => approvals[bucket.id] === true);
  });
  const selectedLessonPlanIdByBucket = getSelectedLessonPlanIdByBucket(selectedByBucket);
  const selectedPreviewHomeworkIdByBucket = Object.fromEntries(
    JCQ_BUCKETS.map((bucket) => [bucket.id, String(selectedByBucket?.[bucket.id]?.previewHomeworkId || selectedByBucket?.[bucket.id]?.homeworkId || '')])
  );
  const publishedAssignmentByBucket = Object.fromEntries(
    (publishedBucketAssignments || []).map((assignment) => [assignment.bucketId, String(assignment.lessonPlanId || '')])
  );
  const publishedMatchesCurrentSelection = JCQ_BUCKETS.every((bucket) => {
    const selectedLessonPlanId = selectedLessonPlanIdByBucket[bucket.id];
    const publishedLessonPlanId = publishedAssignmentByBucket[bucket.id];
    const selectedPreviewHomeworkId = selectedPreviewHomeworkIdByBucket[bucket.id];
    const publishedPreviewHomeworkId = String((publishedBucketAssignments || []).find((assignment) => assignment.bucketId === bucket.id)?.previewHomeworkId || '');
    return Boolean(selectedLessonPlanId)
      && selectedLessonPlanId === publishedLessonPlanId
      && selectedPreviewHomeworkId
      && selectedPreviewHomeworkId === publishedPreviewHomeworkId;
  });
  const testLinksAvailable = publishedMatchesCurrentSelection && hasAnyPublishedBucketLink(publishedLinksByStudent);
  // Once published, the already-approved content can be re-published to apply updated
  // settings (word processor ticks, extended writing question) without re-approval.
  const canPublish = Boolean(selectedClassId) && selectedBucketCount === 3 && (hasFullyApprovedStudent || testLinksAvailable) && !publishing;
  const publishBlockReason = testLinksAvailable ? null
    : !selectedClassId ? 'No class selected'
    : selectedBucketCount !== 3 ? `Only ${selectedBucketCount}/3 buckets selected`
    : !hasFullyApprovedStudent ? 'Approve A, B and C for at least one student first'
    : null;
  const activeBuildBucket = JCQ_BUCKETS.find((bucket) => bucket.id === activeBuildBucketId) || JCQ_BUCKETS[0];
  const activeBuildSelection = selectedByBucket[activeBuildBucket.id];
  const activeBuildHomeworkId = activeBuildSelection?.previewHomeworkId || activeBuildSelection?.homeworkId || '';

  const handleChooseFromContentLibrary = (bucket) => {
    navigate('/content-library', {
      state: {
        jcqMode: true,
        jcqBucketId: bucket.id,
        jcqReturnPath: '/jcq/extra-time-test',
        yearGroup: 'Year 11'
      }
    });
  };

  const handleClassChange = (event) => {
    setSelectedClassId(event.target.value);
    setTargetStudentIds([]);
    setWordProcessorStudentIds([]);
    setApprovedByStudent({});
    setPublishedLinksByStudent({});
    setPublishedBucketAssignments([]);
    setPublishedExtendedWritingPrompt(null);
    setPublishedTestLink('');
  };

  const clearSavedSelections = () => {
    localStorage.removeItem(getJcqFormStorageKey());
    setSelectedClassId('');
    setStudents([]);
    setTargetStudentIds([]);
    setWordProcessorStudentIds([]);
    setExtendedWritingPrompt(null);
    setPublishedExtendedWritingPrompt(null);
    setSelectedByBucket({});
    setActiveBuildBucketId(JCQ_BUCKETS[0].id);
    setScannedBucketIds([]);
    setApprovedByStudent({});
    setPublishedTestLink('');
    setPublishedLinksByStudent({});
    setPublishedBucketAssignments([]);
    setSuccessMessage('');
    setError('');
  };

  const removeBucketSelection = (bucketId) => {
    setSelectedByBucket((prev) => {
      const next = { ...prev };
      delete next[bucketId];
      return next;
    });
    if (activeBuildBucketId === bucketId) {
      const fallback = JCQ_BUCKETS.find((bucket) => bucket.id !== bucketId && selectedByBucket[bucket.id]);
      setActiveBuildBucketId(fallback?.id || JCQ_BUCKETS[0].id);
    }
  };

  const toggleStudentExtraTime = (studentId) => {
    setTargetStudentIds((previous) => (
      previous.includes(studentId)
        ? previous.filter((id) => id !== studentId)
        : [...previous, studentId]
    ));
  };

  const toggleStudentWordProcessor = (studentId) => {
    setWordProcessorStudentIds((previous) => (
      previous.includes(studentId)
        ? previous.filter((id) => id !== studentId)
        : [...previous, studentId]
    ));
  };

  const escapeHtml = (value = '') => String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

  const handlePrintWritingTest = (student) => {
    const prompt = publishedExtendedWritingPrompt || extendedWritingPrompt;
    if (!prompt?.question) return;
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    const marks = Number.isFinite(Number(prompt.marks)) ? Number(prompt.marks) : null;
    printWindow.document.write(`<!DOCTYPE html>
<html>
<head>
<title>JCQ Extended Writing Test</title>
<style>
  body { font-family: Arial, sans-serif; color: #2D4042; margin: 48px; }
  .meta { display: flex; justify-content: space-between; border-bottom: 3px solid #2D4042; padding-bottom: 12px; margin-bottom: 24px; }
  .question { font-size: 1.1rem; font-weight: 700; line-height: 1.6; white-space: pre-wrap; margin-bottom: 8px; }
  .marks { font-weight: 800; margin-bottom: 32px; }
  .lines { border-bottom: 1.5px solid #B9C4C5; height: 34px; }
</style>
</head>
<body>
  <div class="meta">
    <div><strong>Student:</strong> ${escapeHtml(student?.name || '')}</div>
    <div><strong>JCQ Extended Writing Test</strong>${prompt.sourceSubject ? ` · ${escapeHtml(prompt.sourceSubject)}` : ''}</div>
  </div>
  <div class="question">${escapeHtml(prompt.question)}</div>
  ${marks ? `<div class="marks">[${marks} marks]</div>` : ''}
  ${Array.from({ length: 28 }).map(() => '<div class="lines"></div>').join('')}
</body>
</html>`);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  const getPublishedBucketLink = (studentId, bucketId) => publishedLinksByStudent?.[studentId]?.[bucketId] || null;
  const handleCopyLink = (url, key) => {
    navigator.clipboard.writeText(url).then(() => {
      setCopiedKey(key);
      setTimeout(() => setCopiedKey((prev) => (prev === key ? null : prev)), 2000);
    }).catch(() => {});
  };

  const openApprovalModal = (student, bucketId) => {
    if (!selectedByBucket[bucketId] || !scannedBucketIds.includes(bucketId) || testLinksAvailable) return;
    setApprovalModal({
      isOpen: true,
      studentId: normalizeId(student),
      bucketId,
      tutorial: bucketPreviewTutorials[bucketId] || null,
      studentName: student.name || 'Unnamed student'
    });
  };

  const closeApprovalModal = () => {
    setApprovalModal({ isOpen: false, studentId: '', bucketId: '', tutorial: null, studentName: '' });
  };

  const confirmApprovalFromModal = () => {
    const { bucketId } = approvalModal;
    if (!bucketId) return;
    setApprovedByStudent((previous) => {
      const next = { ...previous };
      students.forEach((student) => {
        const sid = normalizeId(student);
        if (sid) next[sid] = { ...(next[sid] || {}), [bucketId]: true };
      });
      return next;
    });
    closeApprovalModal();
  };

  const handleApprovalModalScroll = () => {
    const el = approvalScrollRef.current;
    if (!el) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 16) {
      setApprovalModalScrolled(true);
    }
  };

  const handlePublish = async () => {
    if (!canPublish) return;

    if (testLinksAvailable) {
      const confirmed = window.confirm(
        'Re-publishing creates fresh A, B, C and writing tests with new magic links for every student. Existing links stop appearing here and recorded scores move to the previous test. Continue?'
      );
      if (!confirmed) return;
    }

    try {
      setPublishing(true);
      setError('');
      setSuccessMessage('');

      const lessonPlanIds = JCQ_BUCKETS.map((bucket) => selectedByBucket[bucket.id]?.id).filter(Boolean);
      const previewHomeworkIds = JCQ_BUCKETS.map((bucket) => (
        selectedByBucket[bucket.id]?.previewHomeworkId
        || selectedByBucket[bucket.id]?.homeworkId
        || ''
      ));
      console.log('[JCQ] Publishing with:', { classId: selectedClassId, lessonPlanIds, previewHomeworkIds, targetStudentIds });

      const response = await jcqAPI.generateTest({
        classId: selectedClassId,
        lessonPlanIds,
        previewHomeworkIds,
        jcqTargetStudentIds: targetStudentIds,
        jcqWordProcessorStudentIds: wordProcessorStudentIds,
        extendedWritingPrompt: extendedWritingPrompt?.question ? extendedWritingPrompt : null
      });

      console.log('[JCQ] Publish response:', response.data);
      const responseLinks = response.data?.linksByStudent || {};
      const responseBuckets = Array.isArray(response.data?.bucketAssignments) ? response.data.bucketAssignments : [];
      console.log('[JCQ] Links by student:', responseLinks, '| Bucket count:', responseBuckets.length);

      if (Object.keys(responseLinks).length > 0) {
        setPublishedLinksByStudent(responseLinks);
        setPublishedBucketAssignments(responseBuckets);
        setPublishedTestLink('individual-links');
      } else {
        console.warn('[JCQ] POST succeeded but linksByStudent is empty');
      }

      setSuccessMessage('JCQ Extra Time tests published successfully. Each student now has separate A, B and C magic links, with progress and scores recorded per test.');
      setPublishing(false);
      setLinksRefreshKey((k) => k + 1);
    } catch (err) {
      console.error('[JCQ] Failed to publish:', err);
      const backendMsg = err.response?.data?.error || err.response?.data?.message || '';
      const missingPlans = err.response?.data?.missingLessonPlans;
      const detail = missingPlans ? ` Missing MCQs in: ${missingPlans.join(', ')}.` : (backendMsg ? ` ${backendMsg}` : '');
      setError(`We could not publish the JCQ test.${detail} Check the browser console (F12) for details.`);
      setPublishing(false);
    }
  };

  return (
    <div style={{ background: BRAND.cream, color: BRAND.charcoal, fontFamily: "'Inter', sans-serif", minHeight: '100vh', padding: '44px 20px' }}>
      <main style={{ maxWidth: 1120, margin: '0 auto' }}>
        <Link to="/jcq-hub" style={{ color: BRAND.charcoal, display: 'inline-block', fontWeight: 800, marginBottom: 24, textDecoration: 'none' }}>← Back to JCQ hub</Link>

        <section style={{ ...cardStyle, marginBottom: 24, overflow: 'hidden', position: 'relative' }}>
          <div style={{ background: BRAND.coral, borderRadius: '50%', height: 180, opacity: 0.14, position: 'absolute', right: -50, top: -70, width: 180 }} />
          <p style={{ color: BRAND.coral, fontWeight: 900, letterSpacing: '0.08em', margin: '0 0 8px', textTransform: 'uppercase' }}>JCQ Compliance Builder</p>
          <h1 style={{ fontFamily: 'Nunito, sans-serif', fontSize: 'clamp(2rem, 4vw, 3.4rem)', lineHeight: 1.05, margin: '0 0 12px', position: 'relative' }}>
            Generate JCQ Extra Time Evidence
          </h1>
          <p style={{ color: BRAND.muted, fontSize: '1.08rem', lineHeight: 1.6, margin: 0, maxWidth: 760, position: 'relative' }}>
            Compile a 3-subject baseline assessment to formally quantify processing speed deficits.
          </p>
        </section>

        {error && (
          <div role="alert" style={{ background: '#FFF3F0', border: `2px solid ${BRAND.coral}`, borderRadius: 14, color: BRAND.charcoal, fontWeight: 800, marginBottom: 20, padding: '14px 16px' }}>
            {error}
          </div>
        )}

        {successMessage && (
          <div role="status" style={{ background: '#EAF7EA', border: `2px solid ${BRAND.success}`, borderRadius: 14, color: BRAND.charcoal, fontWeight: 800, marginBottom: 20, padding: '14px 16px' }}>
            ✅ {successMessage}
          </div>
        )}

        <section style={{ ...cardStyle, marginBottom: 24 }}>
          <span style={stepBadgeStyle}>Step 1 · Select Class</span>
          <p style={{ color: BRAND.muted, lineHeight: 1.55, margin: '14px 0 16px' }}>
            Choose the class that will sit the baseline assessment. You will assign extra time overrides to individual students in Step 2.
          </p>
          <select
            value={selectedClassId}
            onChange={handleClassChange}
            disabled={initialLoading}
            style={{
              background: BRAND.white,
              border: `2px solid ${BRAND.charcoal}`,
              borderRadius: 12,
              color: BRAND.charcoal,
              fontSize: '1rem',
              fontWeight: 800,
              maxWidth: 520,
              padding: '14px 16px',
              width: '100%'
            }}
          >
            <option value="">{initialLoading ? 'Loading classes...' : 'Choose a class'}</option>
            {classes.map((classItem) => {
              const classId = normalizeId(classItem);
              return (
                <option key={classId} value={classId}>
                  {classItem.name || 'Unnamed class'}{classItem.yearGroup ? ` · ${classItem.yearGroup}` : ''}{typeof classItem.studentCount === 'number' ? ` · ${classItem.studentCount} students` : ''}
                </option>
              );
            })}
          </select>
        </section>

        <section style={{ ...cardStyle, marginBottom: 24 }}>
          <span style={stepBadgeStyle}>Step 2 · Target Students</span>
          <p style={{ color: BRAND.muted, lineHeight: 1.55, margin: '14px 0 16px' }}>
            Review the class list below. Tick the students who require the 25% extra time override — all others will sit the standard-time baseline.
          </p>

          {!selectedClassId ? (
            <div style={{ background: BRAND.grey, borderRadius: 14, color: BRAND.muted, fontWeight: 800, padding: 18 }}>
              Select a class in Step 1 to load its students.
            </div>
          ) : studentsLoading ? (
            <div style={{ background: BRAND.grey, borderRadius: 14, color: BRAND.muted, fontWeight: 800, padding: 18 }}>
              Loading students...
            </div>
          ) : students.length === 0 ? (
            <div style={{ background: BRAND.grey, borderRadius: 14, color: BRAND.muted, fontWeight: 800, padding: 18 }}>
              No students are currently enrolled in this class.
            </div>
          ) : (
            <div style={{ border: '2px solid #E0E0E0', borderRadius: '12px', overflow: 'hidden' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 0.8fr 0.9fr 1.7fr 0.7fr 1.1fr 1.3fr', background: '#F4F7F6', borderBottom: '2px solid #E0E0E0', fontSize: '0.8em', textTransform: 'uppercase', letterSpacing: '0.8px', fontWeight: '800', color: '#666' }}>
                <div style={{ padding: '12px 14px' }}>Student Name</div>
                <div style={{ padding: '12px 10px' }}>Age</div>
                <div style={{ padding: '12px 10px' }}>RA</div>
                <div style={{ padding: '12px 10px' }}>SEN</div>
                <div style={{ padding: '12px 10px' }}>EAL</div>
                <div style={{ padding: '12px 10px', textAlign: 'center' }}>Extra Time</div>
                <div style={{ padding: '12px 10px', textAlign: 'center' }} title="Ticked students type their extended writing response via magic link; others sit the printed version">Word Processor</div>
              </div>
              {students.map((student, idx) => {
                const studentId = normalizeId(student);
                const checked = targetStudentIds.includes(studentId);
                const wordProcessorChecked = wordProcessorStudentIds.includes(studentId);
                return (
                  <div
                    key={studentId}
                    onClick={() => toggleStudentExtraTime(studentId)}
                    style={{ display: 'grid', gridTemplateColumns: '2fr 0.8fr 0.9fr 1.7fr 0.7fr 1.1fr 1.3fr', borderBottom: idx === students.length - 1 ? 'none' : '1px solid #EAEAEA', background: checked ? '#EAF7EA' : (idx % 2 === 0 ? 'white' : '#FCFCFC'), alignItems: 'center', cursor: 'pointer' }}
                  >
                    <div style={{ padding: '12px 14px', fontWeight: '700', color: BRAND.charcoal }}>
                      {student.name}
                      {student.email && (
                        <div style={{ fontSize: '0.78rem', color: '#64748B', fontWeight: '600', marginTop: '2px' }}>{student.email}</div>
                      )}
                      {student.audioNarrationEnabled && (
                        <span style={{ marginLeft: '10px', fontSize: '0.7em', background: BRAND.success, color: 'white', padding: '2px 8px', borderRadius: '10px', verticalAlign: 'middle', fontWeight: '800' }}>AUDIO</span>
                      )}
                    </div>
                    <div style={{ padding: '12px 10px', color: '#555', fontWeight: '600' }}>{student.age}</div>
                    <div style={{ padding: '12px 10px', color: '#555', fontWeight: '600' }}>{student.readingAgeLabel || student.readingAge}</div>
                    <div style={{ padding: '12px 10px', color: student.senCondition !== 'None' ? BRAND.danger : '#666', fontWeight: student.senCondition !== 'None' ? '700' : '500', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={student.senCondition}>
                      {student.senCondition}
                    </div>
                    <div style={{ padding: '12px 10px', color: student.eal ? BRAND.teal : '#666', fontWeight: student.eal ? '800' : '500' }}>
                      {student.eal ? 'Yes' : 'No'}
                    </div>
                    <div style={{ padding: '12px 10px', textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleStudentExtraTime(studentId)}
                        onClick={(e) => e.stopPropagation()}
                        style={{ accentColor: BRAND.teal, height: 18, width: 18, cursor: 'pointer' }}
                        title={checked ? 'Remove extra time override' : 'Apply 25% extra time override'}
                      />
                    </div>
                    <div style={{ padding: '12px 10px', textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={wordProcessorChecked}
                        onChange={() => toggleStudentWordProcessor(studentId)}
                        onClick={(e) => e.stopPropagation()}
                        style={{ accentColor: BRAND.coral, height: 18, width: 18, cursor: 'pointer' }}
                        title={wordProcessorChecked ? 'Student will type the extended writing test via magic link' : 'Student will sit the printed extended writing test'}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        <section style={{ ...cardStyle, marginBottom: 24 }}>
          <div style={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'space-between' }}>
            <span style={stepBadgeStyle}>Step 3 · Select Subjects</span>
            <div style={{ alignItems: 'center', display: 'flex', gap: 12 }}>
              <strong style={{ color: selectedBucketCount === 3 ? BRAND.success : BRAND.coral }}>
                {selectedBucketCount}/3 selected
              </strong>
              {(selectedClassId || targetStudentIds.length > 0 || selectedBucketCount > 0) && (
                <button
                  type="button"
                  onClick={clearSavedSelections}
                  style={{ background: 'none', border: 'none', color: BRAND.muted, cursor: 'pointer', fontSize: '0.82rem', fontWeight: 800, padding: 0, textDecoration: 'underline' }}
                >
                  Clear saved setup
                </button>
              )}
            </div>
          </div>
          <p style={{ color: BRAND.muted, lineHeight: 1.55, margin: '14px 0 20px' }}>
            Pick one lesson for each subject bucket — either from your dashboard or from the content library.
          </p>

          <div style={{ display: 'grid', gap: 18, gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))' }}>
            {JCQ_BUCKETS.map((bucket) => {
              const selection = selectedByBucket[bucket.id];
              const bucketComplete = Boolean(selection);

              return (
                <div key={bucket.id} style={{ background: BRAND.cream, border: `2px solid ${bucketComplete ? BRAND.success : BRAND.border}`, borderRadius: 16, padding: 18 }}>
                  <div style={{ alignItems: 'flex-start', display: 'flex', gap: 12, justifyContent: 'space-between', marginBottom: 12 }}>
                    <div>
                      <h2 style={{ fontFamily: 'Nunito, sans-serif', fontSize: '1.28rem', margin: 0 }}>{bucket.title}</h2>
                    </div>
                    <span style={{ color: bucketComplete ? BRAND.success : BRAND.muted, fontSize: '1.35rem', fontWeight: 900 }}>
                      {bucketComplete ? '✓' : '○'}
                    </span>
                  </div>

                  {bucketComplete ? (
                    <div style={{ background: BRAND.white, border: `2px solid ${BRAND.success}`, borderRadius: 12, padding: '12px 14px' }}>
                      <div style={{ color: BRAND.charcoal, fontWeight: 900, lineHeight: 1.25 }}>{selection.title || 'Selected tutorial'}</div>
                      <div style={{ color: BRAND.muted, fontSize: '0.84rem', fontWeight: 700, marginTop: 4 }}>
                        {selection.subject || bucket.subtitle}{selection.yearGroup ? ` · ${selection.yearGroup}` : ''}
                      </div>
                      {selection.homeworkId && (
                        <div style={{ background: '#EAF6F7', borderRadius: 10, color: BRAND.charcoal, fontSize: '0.78rem', fontWeight: 800, marginTop: 10, padding: '7px 9px' }}>
                          Ready for Step 4 build + preview
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() => removeBucketSelection(bucket.id)}
                        style={{ background: 'none', border: 'none', color: BRAND.muted, cursor: 'pointer', fontSize: '0.8rem', fontWeight: 700, marginTop: 8, padding: 0, textDecoration: 'underline' }}
                      >
                        Remove selection
                      </button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      <button
                        type="button"
                        disabled
                        style={{ background: '#F0F2F5', border: `2px solid ${BRAND.border}`, borderRadius: 12, color: '#8A9A9C', cursor: 'not-allowed', fontWeight: 800, opacity: 0.7, padding: '12px 14px', textAlign: 'left', width: '100%' }}
                      >
                        📋 Choose from Dashboard
                        <span style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, marginTop: 3 }}>Coming soon</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => handleChooseFromContentLibrary(bucket)}
                        style={{ background: BRAND.teal, border: `2px solid ${BRAND.charcoal}`, borderRadius: 12, color: 'white', cursor: 'pointer', fontWeight: 800, padding: '12px 14px', textAlign: 'left', width: '100%' }}
                      >
                        📚 Choose from Content Library
                        <span style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, marginTop: 3, opacity: 0.85 }}>Year 11 · {bucket.subjects.slice(0, 2).join(', ')}{bucket.subjects.length > 2 ? '…' : ''}</span>
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        <section style={{ ...cardStyle, marginBottom: 24 }}>
          <div style={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'space-between' }}>
            <span style={stepBadgeStyle}>Step 4 · Build Test</span>
            <strong style={{ color: selectedBucketCount === 3 ? BRAND.success : BRAND.muted }}>
              {selectedBucketCount === 3 ? 'Ready to build' : 'Select all 3 buckets first'}
            </strong>
          </div>
          <p style={{ color: BRAND.muted, lineHeight: 1.55, margin: '14px 0 20px' }}>
            Build each selected tutorial using the same Tutorial Content, Tutorial Structure, and dynamic Tutorial Preview experience used on homework and content-library pages.
          </p>

          {selectedBucketCount === 0 ? (
            <div style={{ background: BRAND.cream, border: `2px dashed ${BRAND.border}`, borderRadius: 14, color: BRAND.muted, fontWeight: 800, padding: 18 }}>
              Choose a tutorial in Step 3 to load the builder here.
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 18 }}>
                {JCQ_BUCKETS.map((bucket) => {
                  const selection = selectedByBucket[bucket.id];
                  const disabled = !selection;
                  const active = activeBuildBucket.id === bucket.id;
                  return (
                    <button
                      key={bucket.id}
                      type="button"
                      disabled={disabled}
                      onClick={() => setActiveBuildBucketId(bucket.id)}
                      style={{
                        background: active ? BRAND.teal : (disabled ? '#EEF2F3' : BRAND.white),
                        border: `2px solid ${active ? BRAND.charcoal : BRAND.border}`,
                        borderRadius: 12,
                        color: disabled ? '#8A9A9C' : (active ? BRAND.white : BRAND.charcoal),
                        cursor: disabled ? 'not-allowed' : 'pointer',
                        fontWeight: 900,
                        padding: '10px 14px'
                      }}
                    >
                      {bucket.title}{selection ? ` · ${selection.subject || bucket.subtitle}` : ' · not selected'}
                    </button>
                  );
                })}
              </div>

              {activeBuildHomeworkId ? (
                <div style={{ border: `2px solid ${BRAND.charcoal}`, borderRadius: 18, overflow: 'hidden', background: BRAND.cream }}>
                  <div style={{ background: BRAND.charcoal, color: BRAND.white, padding: '12px 16px', fontWeight: 900 }}>
                    Building {activeBuildBucket.title}: {activeBuildSelection.title}
                  </div>
                  <DemoHomeworkDetail
                    user={null}
                    onLogout={() => {}}
                    homeworkId={activeBuildHomeworkId}
                    libraryPreview={true}
                    libraryEntryId={activeBuildSelection.libraryEntryId || ''}
                    libraryLessonTitle={activeBuildSelection.title || ''}
                    hideLibraryAddAction={true}
                    forceBuildControlsUnlocked={true}
                    jcqEmbeddedBuildMode={true}
                    onStudentTutorialsLoaded={(tutorials) => {
                      const completed = tutorials.find((t) => t.status === 'COMPLETED' && t.generatedContent?.length > 0) || tutorials[0] || null;
                      if (completed) setBucketPreviewTutorials((prev) => ({ ...prev, [activeBuildBucketId]: completed }));
                      setScannedBucketIds((prev) => prev.includes(activeBuildBucketId) ? prev : [...prev, activeBuildBucketId]);
                    }}
                    onStudentSlideshowsLoaded={(slideshows) => {
                      setBucketPreviewSlideshows((prev) => ({ ...prev, [activeBuildBucketId]: slideshows }));
                    }}
                  />
                </div>
              ) : (
                <div style={{ background: '#FFF8E1', border: `2px solid ${BRAND.warning}`, borderRadius: 14, color: BRAND.charcoal, fontWeight: 800, padding: 18 }}>
                  This selection is saved, but its preview builder is not available yet. Re-open it from the content library and click "Add to JCQ Test" again to attach the live preview.
                </div>
              )}
            </>
          )}
        </section>

        <section style={{ ...cardStyle, marginBottom: 24 }}>
          <div style={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'space-between' }}>
            <span style={stepBadgeStyle}>Step 4.1 · Extended Writing Test</span>
            <strong style={{ color: extendedWritingPrompt?.question ? BRAND.success : BRAND.muted }}>
              {extendedWritingPrompt?.question ? 'Writing question saved' : 'Optional'}
            </strong>
          </div>
          <p style={{ color: BRAND.muted, lineHeight: 1.55, margin: '14px 0 20px' }}>
            Generate or write an extended writing question. When you publish in Step 5 it becomes a standalone writing test — students ticked as Word Processor in Step 2 get a magic link to type their response; everyone else sits the printed version.
          </p>
          <ExtendedWritingTestBuilder
            bucketASelection={selectedByBucket.BUCKET_A || null}
            savedPrompt={extendedWritingPrompt}
            onSave={setExtendedWritingPrompt}
            onClear={() => setExtendedWritingPrompt(null)}
          />
        </section>

        <section style={{ ...cardStyle, marginBottom: 24 }}>
          <div style={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'space-between' }}>
            <span style={stepBadgeStyle}>Step 5 · Publish Test</span>
            <strong style={{ color: hasFullyApprovedStudent ? BRAND.success : BRAND.muted }}>
              {testLinksAvailable ? 'Published — re-publish to apply changes' : hasFullyApprovedStudent ? 'Ready to publish' : 'Approve A, B and C first'}
            </strong>
          </div>
          <p style={{ color: BRAND.muted, lineHeight: 1.55, margin: '14px 0 18px' }}>
            Scan the built content in Step 4, then approve Buckets A, B and C. Approving a bucket approves that same bucket for every student; publishing creates individual student magic links for A, B and C so each score is recorded separately.
          </p>
          {publishedLinksLoading && (
            <div style={{ background: '#F8FAFC', border: `1px solid ${BRAND.border}`, borderRadius: 12, color: BRAND.muted, fontWeight: 800, marginBottom: 12, padding: 12 }}>
              Loading existing JCQ magic links and scores…
            </div>
          )}
          {!publishedLinksLoading && publishedBucketAssignments.length > 0 && publishedMatchesCurrentSelection && (
            <div style={{ background: '#EAF7EA', border: `1px solid ${BRAND.success}`, borderRadius: 12, color: BRAND.success, fontWeight: 900, marginBottom: 12, padding: 12 }}>
              {publishedBucketAssignments.length} JCQ bucket tests are published for this class. Scores below update after students complete their individual links.
            </div>
          )}

          {publishStudents.length === 0 ? (
            <div style={{ background: BRAND.cream, border: `2px dashed ${BRAND.border}`, borderRadius: 14, color: BRAND.muted, fontWeight: 800, padding: 18 }}>
              Select a class in Step 1 to show student approval rows.
            </div>
          ) : (
            <div style={{ display: 'grid', gap: 10 }}>
              {publishStudents.map((student) => {
                const studentId = normalizeId(student);
                const approvals = approvedByStudent[studentId] || {};
                const rowApproved = JCQ_BUCKETS.every((bucket) => approvals[bucket.id] === true);
                const isExtraTimeStudent = targetStudentIds.includes(studentId);

                return (
                  <div key={studentId} className="dash-card" style={{ padding: '12px 14px', marginBottom: 0, background: rowApproved ? '#DFF6E4' : 'white', border: rowApproved ? '2px solid #81C784' : undefined, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ alignItems: 'center', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ color: BRAND.charcoal, fontWeight: 900 }}>{student.name || 'Unnamed student'}</span>
                        {isExtraTimeStudent && (
                          <span style={{ background: '#F0FDFA', border: `1px solid ${BRAND.teal}`, borderRadius: '999px', color: BRAND.teal, fontSize: '0.72rem', fontWeight: 900, padding: '3px 8px' }}>+25% EXTRA TIME</span>
                        )}
                        {rowApproved && (
                          <span style={{ background: '#EAF7EA', border: `1px solid ${BRAND.success}`, borderRadius: '999px', color: BRAND.success, fontSize: '0.72rem', fontWeight: 900, padding: '3px 8px' }}>APPROVED</span>
                        )}
                      </div>
                      <div style={{ color: BRAND.muted, fontSize: '0.82rem', fontWeight: 700, marginTop: 4 }}>
                        {testLinksAvailable ? 'Individual A/B/C magic links are available for this student.' : 'Approve A, B and C after scanning the matching bucket preview.'}
                      </div>
                    </div>

                    <div style={{ alignItems: 'center', display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                      {JCQ_BUCKETS.map((bucket) => {
                        const bucketApproved = approvals[bucket.id] === true;
                        const bucketReady = Boolean(selectedByBucket[bucket.id]) && scannedBucketIds.includes(bucket.id);
                        const publishedBucketLink = getPublishedBucketLink(studentId, bucket.id);
                        const bucketUrl = publishedMatchesCurrentSelection ? (publishedBucketLink?.url || '') : '';
                        if (bucketUrl) {
                          const copyKey = `${studentId}-${bucket.id}`;
                          const attempt = publishedBucketLink?.attempt || null;
                          const scoreLabel = attempt
                            ? `${attempt.status === 'completed' ? 'Done' : 'Started'} · ${attempt.score}/${attempt.totalQuestions}${attempt.scorePercent !== null && attempt.scorePercent !== undefined ? ` (${attempt.scorePercent}%)` : ''}`
                            : 'Not started';
                          return (
                            <div key={copyKey} style={{ alignItems: 'center', display: 'flex', flexDirection: 'column', gap: 4, minWidth: 90 }}>
                              <div style={{ display: 'flex', gap: 4 }}>
                                <a
                                  href={bucketUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                  style={{ background: BRAND.teal, border: `2px solid ${BRAND.charcoal}`, borderRadius: '12px 0 0 12px', color: BRAND.white, fontWeight: 950, padding: '10px 12px', textDecoration: 'none' }}
                                >
                                  {getBucketLetter(bucket.id)}
                                </a>
                                <button
                                  type="button"
                                  onClick={() => handleCopyLink(bucketUrl, copyKey)}
                                  title="Copy link"
                                  style={{ background: copiedKey === copyKey ? BRAND.success : BRAND.charcoal, border: `2px solid ${BRAND.charcoal}`, borderRadius: '0 12px 12px 0', color: BRAND.white, cursor: 'pointer', fontWeight: 900, padding: '10px 10px' }}
                                >
                                  {copiedKey === copyKey ? '✓' : '⎘'}
                                </button>
                              </div>
                              <span style={{ color: attempt?.status === 'completed' ? BRAND.success : BRAND.muted, fontSize: '0.72rem', fontWeight: 900 }}>
                                {scoreLabel}
                              </span>
                            </div>
                          );
                        }
                        return (
                          <button
                            key={`${studentId}-${bucket.id}`}
                            type="button"
                            onClick={() => openApprovalModal(student, bucket.id)}
                            disabled={!bucketReady || bucketApproved || testLinksAvailable}
                            title={bucketReady ? `Approve ${bucket.title}` : `Open and scan ${bucket.title} in Step 4 first`}
                            style={{
                              background: bucketApproved ? '#EAF7EA' : (bucketReady ? BRAND.white : '#EEF2F3'),
                              border: `2px solid ${bucketApproved ? BRAND.success : (bucketReady ? BRAND.charcoal : BRAND.border)}`,
                              borderRadius: 12,
                              color: bucketApproved ? BRAND.success : (bucketReady ? BRAND.charcoal : BRAND.muted),
                              cursor: (!bucketReady || bucketApproved || testLinksAvailable) ? 'not-allowed' : 'pointer',
                              fontWeight: 950,
                              padding: '9px 12px'
                            }}
                          >
                            {bucketApproved ? `✓ ${getBucketLetter(bucket.id)}` : `Approve ${getBucketLetter(bucket.id)}`}
                          </button>
                        );
                      })}
                      {(() => {
                        const writingLink = publishedMatchesCurrentSelection ? getPublishedBucketLink(studentId, 'BUCKET_WRITING') : null;
                        if (!writingLink?.url) return null;
                        const writingPrompt = publishedExtendedWritingPrompt || extendedWritingPrompt;
                        if (writingLink.wordProcessor) {
                          const copyKey = `${studentId}-BUCKET_WRITING`;
                          const attempt = writingLink.attempt || null;
                          return (
                            <div key={copyKey} style={{ alignItems: 'center', display: 'flex', flexDirection: 'column', gap: 4, minWidth: 90 }}>
                              <div style={{ display: 'flex', gap: 4 }}>
                                <a
                                  href={writingLink.url}
                                  target="_blank"
                                  rel="noreferrer"
                                  title="Extended Writing test (typed via magic link)"
                                  style={{ background: BRAND.coral, border: `2px solid ${BRAND.charcoal}`, borderRadius: '12px 0 0 12px', color: BRAND.white, fontWeight: 950, padding: '10px 12px', textDecoration: 'none' }}
                                >
                                  W
                                </a>
                                <button
                                  type="button"
                                  onClick={() => handleCopyLink(writingLink.url, copyKey)}
                                  title="Copy writing test link"
                                  style={{ background: copiedKey === copyKey ? BRAND.success : BRAND.charcoal, border: `2px solid ${BRAND.charcoal}`, borderRadius: '0 12px 12px 0', color: BRAND.white, cursor: 'pointer', fontWeight: 900, padding: '10px 10px' }}
                                >
                                  {copiedKey === copyKey ? '✓' : '⎘'}
                                </button>
                              </div>
                              <span style={{ color: attempt?.status === 'completed' ? BRAND.success : BRAND.muted, fontSize: '0.72rem', fontWeight: 900 }}>
                                {attempt ? (attempt.status === 'completed' ? 'Submitted' : 'Started') : 'Word processor'}
                              </span>
                            </div>
                          );
                        }
                        return (
                          <div key={`${studentId}-BUCKET_WRITING`} style={{ alignItems: 'center', display: 'flex', flexDirection: 'column', gap: 4, minWidth: 90 }}>
                            <button
                              type="button"
                              onClick={() => handlePrintWritingTest(student)}
                              disabled={!writingPrompt?.question}
                              title={writingPrompt?.question ? 'Print the extended writing test for this student' : 'Writing question unavailable'}
                              style={{ background: writingPrompt?.question ? BRAND.white : '#EEF2F3', border: `2px solid ${BRAND.charcoal}`, borderRadius: 12, color: BRAND.charcoal, cursor: writingPrompt?.question ? 'pointer' : 'not-allowed', fontWeight: 950, padding: '9px 12px' }}
                            >
                              🖨 Print W
                            </button>
                            <span style={{ color: BRAND.muted, fontSize: '0.72rem', fontWeight: 900 }}>Printed test</span>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div style={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 14, justifyContent: 'space-between', marginTop: 18 }}>
            <p style={{ color: BRAND.muted, fontWeight: 800, margin: 0 }}>
              Required: one class, exactly three lesson plans, and approved A, B and C tests.
              {publishBlockReason && (
                <span style={{ color: BRAND.coral, display: 'block', fontSize: '0.85rem', marginTop: 4 }}>
                  Blocked: {publishBlockReason}
                </span>
              )}
            </p>
            <button
              type="button"
              onClick={handlePublish}
              disabled={!canPublish}
              style={{
                background: canPublish ? BRAND.coral : '#D8DEDF',
                border: `2px solid ${BRAND.charcoal}`,
                borderRadius: 14,
                boxShadow: canPublish ? '5px 5px 0 rgba(45,64,66,0.2)' : 'none',
                color: canPublish ? BRAND.white : BRAND.muted,
                cursor: canPublish ? 'pointer' : 'not-allowed',
                fontSize: '1.05rem',
                fontWeight: 950,
                padding: '16px 26px'
              }}
            >
              {publishing ? 'Publishing...' : testLinksAvailable ? 'Re-publish Test' : 'Publish JCQ Test'}
            </button>
          </div>
        </section>

        <JCQReport classId={selectedClassId} testLinksAvailable={testLinksAvailable} />

        <TutorialReviewModal
          isOpen={approvalModal.isOpen}
          onClose={closeApprovalModal}
          studentName={`${approvalModal.studentName} — Bucket ${getBucketLetter(approvalModal.bucketId)}`}
          tutorial={approvalModal.tutorial}
          slideshows={(bucketPreviewSlideshows[approvalModal.bucketId] || []).filter((ss) => ss.studentTutorialId?.toString() === approvalModal.tutorial?._id?.toString())}
          requireApproval={true}
          alreadyApproved={approvedByStudent[approvalModal.studentId]?.[approvalModal.bucketId] === true}
          onApprove={confirmApprovalFromModal}
        />
      </main>
    </div>
  );
};

export default JCQExtraTimeTest;
